import { useState } from "react";
import { MapPin, Phone, Mail, Send, Clock, ArrowRight } from "lucide-react";

const contactInfo = [
  {
    icon: MapPin,
    label: "Dirección",
    value: "El Cebil N° 417, B° Chijra",
    extra: "San Salvador de Jujuy - Argentina",
    href: "https://www.google.com/maps/search/El+Cebil+417+Chijra+San+Salvador+de+Jujuy+Argentina",
  },
  {
    icon: Phone,
    label: "Teléfono",
    value: "+54 388 4801994",
    extra: null,
    href: "tel:+543884801994",
  },
  {
    icon: Mail,
    label: "Email",
    value: "contacto@norobras.com",
    extra: null,
    href: "mailto:contacto@norobras.com",
  },
  {
    icon: Clock,
    label: "Horario de atención",
    value: "Lunes a Viernes",
    extra: "08:00 - 17:00 hs",
    href: null,
  },
];

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 4000);
  };

  return (
    <section id="contact" className="bg-slate-900 py-24">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-orange-500 text-sm font-semibold uppercase tracking-widest mb-3">
            Contacto
          </p>
          <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
            Hablemos de tu{" "}
            <span className="text-orange-500">próximo proyecto</span>
          </h2>
          <p className="text-slate-400 max-w-xl mx-auto text-base leading-relaxed">
            Estamos listos para escucharte. Contanos tu necesidad y te
            brindamos una solución a medida.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Contact Info + Map (left 2 cols) */}
          <div className="lg:col-span-2 space-y-6">
            {/* Info Cards */}
            {contactInfo.map((item, index) => {
              const Icon = item.icon;
              const Wrapper = item.href ? "a" : "div";
              const wrapperProps = item.href
                ? { href: item.href, target: "_blank", rel: "noopener noreferrer" }
                : {};
              return (
                <Wrapper
                  key={index}
                  {...wrapperProps}
                  className={`flex items-start gap-4 bg-slate-800/50 border border-slate-700/50 rounded-xl p-5 transition-all duration-300 ${
                    item.href
                      ? "hover:border-orange-500/40 hover:bg-slate-800 cursor-pointer group"
                      : ""
                  }`}
                >
                  <div className="bg-orange-500/10 border border-orange-500/20 w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon className="text-orange-500" size={20} />
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs uppercase tracking-wider font-semibold mb-1">
                      {item.label}
                    </p>
                    <p className="text-white text-sm font-medium group-hover:text-orange-400 transition-colors">
                      {item.value}
                    </p>
                    {item.extra && (
                      <p className="text-slate-400 text-sm">{item.extra}</p>
                    )}
                  </div>
                </Wrapper>
              );
            })}

            {/* Map */}
            <div className="rounded-xl overflow-hidden border border-slate-700/50 h-[220px]">
              <iframe
                title="Ubicación Nor Obras"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3639.9863117056134!2d-65.30092141281622!3d-24.17221282857127!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x941b0f653824456b%3A0x137d1d5bab48a8d9!2sEl%20Cebil%20417%2C%20Y4600%20San%20Salvador%20de%20Jujuy%2C%20Jujuy!5e0!3m2!1ses!2sar!4v1773433999916!5m2!1ses!2sar"
                width="100%"
                height="100%"
                style={{ border: 0, filter: "invert(90%) hue-rotate(180deg)" }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Contact Form (right 3 cols) */}
          <div className="lg:col-span-3">
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-8 md:p-10 h-full">
              <h3 className="text-white font-bold text-xl mb-2">
                Envianos tu consulta
              </h3>
              <p className="text-slate-500 text-sm mb-8">
                Completá el formulario y nos pondremos en contacto a la brevedad.
              </p>

              {submitted ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="bg-green-500/10 border border-green-500/30 w-16 h-16 rounded-full flex items-center justify-center mb-5">
                    <Send className="text-green-400" size={28} />
                  </div>
                  <h4 className="text-white font-bold text-lg mb-2">
                    Mensaje enviado
                  </h4>
                  <p className="text-slate-400 text-sm">
                    Gracias por contactarnos. Te responderemos a la brevedad.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="name" className="block text-slate-400 text-xs uppercase tracking-wider font-semibold mb-2">
                        Nombre completo *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full bg-slate-900/60 border border-slate-700 rounded-lg px-4 py-3 text-white text-sm placeholder-slate-600 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-colors"
                        placeholder="Tu nombre"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-slate-400 text-xs uppercase tracking-wider font-semibold mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full bg-slate-900/60 border border-slate-700 rounded-lg px-4 py-3 text-white text-sm placeholder-slate-600 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-colors"
                        placeholder="tu@email.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="phone" className="block text-slate-400 text-xs uppercase tracking-wider font-semibold mb-2">
                        Teléfono
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full bg-slate-900/60 border border-slate-700 rounded-lg px-4 py-3 text-white text-sm placeholder-slate-600 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-colors"
                        placeholder="+54 ..."
                      />
                    </div>
                    <div>
                      <label htmlFor="subject" className="block text-slate-400 text-xs uppercase tracking-wider font-semibold mb-2">
                        Asunto *
                      </label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        required
                        value={formData.subject}
                        onChange={handleChange}
                        className="w-full bg-slate-900/60 border border-slate-700 rounded-lg px-4 py-3 text-white text-sm placeholder-slate-600 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-colors"
                        placeholder="Asunto de tu consulta"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-slate-400 text-xs uppercase tracking-wider font-semibold mb-2">
                      Mensaje *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={5}
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full bg-slate-900/60 border border-slate-700 rounded-lg px-4 py-3 text-white text-sm placeholder-slate-600 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-colors resize-none"
                      placeholder="Describí brevemente tu proyecto o necesidad..."
                    />
                  </div>

                  <button
                    type="submit"
                    className="group inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-8 py-3.5 rounded uppercase tracking-wide text-sm transition-all duration-200 hover:shadow-lg hover:shadow-orange-500/25 hover:-translate-y-0.5"
                  >
                    Enviar consulta
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
